/** Care pass pricing — content + selector options for PricingPlanCard. */

export const HOURLY_PACK_OPTIONS = [
  { id: "1", label: "1 Booking", price: 100, save: null },
  { id: "3", label: "3 Bookings", price: 250, save: 50 },
  { id: "5", label: "5 Bookings", price: 400, save: 100 },
  { id: "7", label: "7 Bookings", price: 550, save: 150 },
  { id: "15", label: "15 Bookings", price: 750, save: 750 },
  { id: "30", label: "30 Bookings", price: 1000, save: 2000 },
];

export const SHORT_TERM_OPTIONS = [
  { id: "3", name: "Mini Care Pass", label: "Mini Care Pass · 3 care days", price: 500, days: 3 },
  { id: "7", name: "Weekly Care Pass", label: "Weekly Care Pass · 7 care days", price: 1000, days: 7 },
  { id: "15", name: "Fortnight Care Pass", label: "Fortnight Care Pass · 15 care days", price: 1500, days: 15 },
  { id: "30", name: "Monthly Care Pass", label: "Monthly Care Pass · 30 care days", price: 2500, days: 30 },
];

/** Display suffix for a short-term pack (e.g. "3 care days"). */
export function shortTermPackDaysLabel(pack) {
  const days = Number(pack?.days || pack?.id);
  if (!Number.isFinite(days) || days <= 0) return "";
  return `${days} care day${days === 1 ? "" : "s"}`;
}

/** Price difference to upgrade from one short-term pack to a higher one. */
export function shortTermUpgradeDelta(fromPackId, toPackId) {
  const from = SHORT_TERM_OPTIONS.find((o) => String(o.id) === String(fromPackId));
  const to = SHORT_TERM_OPTIONS.find((o) => String(o.id) === String(toPackId));
  if (!from || !to || to.price <= from.price) return null;
  return {
    from,
    to,
    amountInr: to.price - from.price,
  };
}

export function formatInr(amount) {
  return Number(amount).toLocaleString("en-IN");
}

const GST_RATE = 0.18;
const CGST_RATE = 0.09;
const SGST_RATE = 0.09;

/** Resolve checkout order summary for a selected care pass. */
export function getCarePassCheckoutOrder({ planId, packId } = {}) {
  const plan = PRICING_PLANS.find((p) => p.id === planId) || PRICING_PLANS.find((p) => p.id === "long");
  const pack =
    plan?.selector?.options?.find((o) => String(o.id) === String(packId)) ||
    plan?.selector?.options?.find((o) => String(o.id) === String(plan.selector?.defaultId)) ||
    null;

  let baseAmount = Number(plan?.price || 0);
  let lineLabel = "Platform subscription";
  let setupFee = 0;
  let exitFee = 0;
  let showLaunchWaivers = false;

  if (plan?.id === "hourly" && pack) {
    baseAmount = Number(pack.price || 0);
    lineLabel = `Hourly booking pack (${pack.label})`;
  } else if (plan?.id === "short" && pack) {
    baseAmount = Number(pack.price || 0);
    lineLabel =
      pack.name
        ? `${pack.name} (${pack.days || pack.id} caregiver days · any dates)`
        : `Short-term marketplace pass (${shortTermPackDaysLabel(pack)})`;
  } else if (plan?.id === "long") {
    baseAmount = 3000;
    lineLabel = "Quarterly Care Pass (3 months continuous)";
    setupFee = 500;
    exitFee = 1000;
    showLaunchWaivers = true;
  } else if (plan?.id === "free") {
    baseAmount = 0;
    lineLabel = "Free care pass (browse only)";
  }

  const waivedTotal = showLaunchWaivers ? setupFee + exitFee : 0;
  const taxable = baseAmount;
  const cgst = Math.round(taxable * CGST_RATE);
  const sgst = Math.round(taxable * SGST_RATE);
  const grandTotal = taxable + cgst + sgst;

  return {
    plan,
    pack,
    planTitle: plan?.title || "NEMA CARE PASS",
    lineLabel,
    baseAmount,
    setupFee,
    exitFee,
    showLaunchWaivers,
    waivedTotal,
    taxable,
    cgst,
    sgst,
    gstRate: GST_RATE,
    grandTotal,
  };
}

export const PRICING_PLANS = [
  {
    id: "carehub-monthly",
    category: "PHYSIO CAREHUB",
    categoryTone: "accent",
    title: "CAREHUB MONTHLY",
    subtitle: "For certified physiotherapists",
    priceType: "simple",
    price: 250,
    priceNote: "1 free booking credit / 30 days",
    priceNoteTone: "muted",
    includedTitle: "WHAT'S INCLUDED",
    included: [
      "Zero-commission home-visit marketplace",
      "1 free booking credit (expires end of cycle)",
      "Search listing while credits remain",
      "GST invoices (18% CGST/SGST)",
    ],
    excluded: [],
    cta: "ACTIVATE MONTHLY",
    ctaTone: "solid",
    audience: "therapist",
  },
  {
    id: "carehub-annual",
    category: "PHYSIO CAREHUB",
    categoryTone: "accent",
    title: "CAREHUB ANNUAL",
    subtitle: "Best value for active practices",
    priceType: "simple",
    price: 2500,
    priceNote: "12 free credits · 1 credited each month",
    priceNoteTone: "muted",
    includedTitle: "WHAT'S INCLUDED",
    included: [
      "Zero-commission home-visit marketplace",
      "12 free booking credits (1/month)",
      "Priority listing while subscribed",
      "GST invoices (18% CGST/SGST)",
    ],
    excluded: [],
    cta: "ACTIVATE ANNUAL",
    ctaTone: "solid",
    audience: "therapist",
  },
  {
    id: "carehub-credits",
    category: "PHYSIO CAREHUB",
    categoryTone: "muted",
    title: "BOOKING CREDIT PACK",
    subtitle: "High-density booking credits",
    priceType: "simple",
    price: 500,
    priceNote: "5 booking credits",
    priceNoteTone: "muted",
    includedTitle: "WHAT'S INCLUDED",
    included: [
      "5 purchased booking credits",
      "Used after free credits are exhausted",
      "Required for search visibility",
    ],
    excluded: [],
    cta: "BUY CREDITS",
    ctaTone: "outline",
    audience: "therapist",
  },
  {
    id: "free",
    category: "STANDARD ENTRY",
    categoryTone: "muted",
    title: "NEMA FREE CARE PASS",
    subtitle: "Explore Before You Decide",
    priceType: "simple",
    price: 0,
    priceNote: "Browse profiles only",
    priceNoteTone: "muted",
    included: [
      "Browse caregiver profiles",
      "Search caregivers",
      "Compare profiles",
      "Save favourites",
      "Basic support",
    ],
    excluded: ["Contact caregiver", "Booking slots", "Priority support"],
    cta: "START FREE",
    ctaTone: "ghost",
  },
  {
    id: "hourly",
    category: "ON-DEMAND CARE",
    categoryTone: "accent",
    title: "NEMA HOURLY CARE PASS",
    subtitle: "Under 2h sessions · Pay caregiver separately",
    priceType: "starting",
    price: 100,
    priceSuffix: "/ Booking",
    priceNote: "Pays caregiver separately",
    priceNoteTone: "success",
    selector: {
      title: "SELECT BOOKING PACK",
      options: HOURLY_PACK_OPTIONS,
      defaultId: "1",
    },
    included: [
      "Verification assistance",
      "Instant booking confirmation",
      "Automated reminders",
      "Customer support helpdesk",
      "Direct caregiver payment",
    ],
    highlights: ["Valid for 1 month", "Valid only for hourly booking"],
    excluded: [],
    cta: "BOOK HOURLY CARE",
    ctaTone: "solid",
  },
  {
    id: "short",
    category: "TEMPORARY RECOVERY",
    categoryTone: "primary",
    title: "NEMA SHORT-TERM CARE PASSES",
    subtitle: "One-Time Marketplace Pass · Start small, upgrade anytime",
    priceType: "duration",
    priceNote: "No recurring subscription · Upgrade by paying only the difference",
    priceNoteTone: "warning",
    selector: {
      title: "CHOOSE YOUR CARE PASS",
      options: SHORT_TERM_OPTIONS,
      defaultId: "3",
    },
    included: [
      "Book caregiver days on consecutive or different dates",
      "A 3-day pass provides 3 eligible service days (usage-based)",
      "Select any available future service dates (caregiver availability applies)",
      "Use your care days until the pool is finished",
      "Unlimited caregiver search",
      "Contact caregiver directly",
      "Replacement assistance",
      "Priority support lines",
      "One-time pass fee",
      "Upgrade to a longer pass anytime — pay only the price difference",
    ],
    excluded: [],
    cta: "CHOOSE SHORT-TERM PASS",
    ctaTone: "primary",
  },
  {
    id: "long",
    category: "ONGOING CARE",
    categoryTone: "primary",
    title: "NEMA QUARTERLY CARE PASS",
    subtitle: "3 months of continuous care access",
    priceType: "subscription",
    price: 3000,
    priceSuffix: "For 3 Months continuous",
    priceNote: "Equivalent to just ₹1,000 per month",
    priceNoteTone: "primary",
    featured: true,
    popular: true,
    launchBenefits: {
      title: "LIMITED PERIOD LAUNCH BENEFITS",
      items: [
        "Setup Fee (₹500) — FREE",
        "Lock-in or Exit Charges — NONE",
      ],
    },
    includedTitle: "WHAT'S INCLUDED",
    included: [
      "3 months continuous marketplace access",
      "Book caregivers anytime during the full 3 months",
      "No day-limit pool — continuous coverage",
      "Unlimited caregiver search",
      "Direct communication with caregivers",
      "Caregiver profiles with verification assistance",
      "Priority customer support",
      "Caregiver replacement assistance",
      "Digital dashboard",
      "Secure payment history",
      "GST invoices",
    ],
    excluded: [],
    cta: "START QUARTERLY CARE PASS",
    ctaTone: "solid",
  },
];

/** Merge authoritative backend fee schedule into static plan cards. */
export function applyBackendPricingPlans(plans = PRICING_PLANS, backend = {}) {
  const hourlyOptions = backend?.hourlyPackOptions;
  const shortOptions = backend?.shortTermOptions;
  const marketplaceAmount = backend?.marketplaceAccess?.PLAN_AMOUNT_INR;
  const therapist = backend?.careHubTherapist || {};

  return plans.map((plan) => {
    if (plan.id === "hourly" && Array.isArray(hourlyOptions) && hourlyOptions.length) {
      return {
        ...plan,
        price: hourlyOptions[0]?.price ?? plan.price,
        selector: { ...plan.selector, options: hourlyOptions },
      };
    }
    if (plan.id === "short" && Array.isArray(shortOptions) && shortOptions.length) {
      return {
        ...plan,
        selector: {
          ...plan.selector,
          options: shortOptions,
          defaultId: shortOptions[0]?.id || plan.selector?.defaultId || "3",
        },
      };
    }
    if (plan.id === "long" && marketplaceAmount != null) {
      return { ...plan, price: marketplaceAmount };
    }
    if (plan.id === "carehub-monthly" && therapist.MONTHLY?.AMOUNT_INR != null) {
      return { ...plan, price: therapist.MONTHLY.AMOUNT_INR };
    }
    if (plan.id === "carehub-annual" && therapist.ANNUAL?.AMOUNT_INR != null) {
      return { ...plan, price: therapist.ANNUAL.AMOUNT_INR };
    }
    if (plan.id === "carehub-credits" && therapist.CREDIT_PACK?.AMOUNT_INR != null) {
      return { ...plan, price: therapist.CREDIT_PACK.AMOUNT_INR };
    }
    return plan;
  });
}
